import React from "react";
import { formatMessage } from "lib/globalize-util";
import { ASSORTMENT_DROPDOWN_WIDTH } from "containers/folders/utils/constants";
import { generatePermissionOptions } from "containers/folders/containers/space-manager/components/manage-access/manage-access-form";
import { ILowerBoundaryRoleMap, PermissionRole, PermissionRanking } from "containers/folders/types/base";
import me from "models/me";
import { IDropdownStyles, IDropdownOption, Dropdown } from "@nuorder/components";
import { StopSharing, comparePermissionRoles } from "containers/folders/utils/permissions";
import { DATATEST } from "constants/integration";
interface IOwnProps {
    item: nuorder.folder.v1.Permission;
    merchantId: string;
    selectedItemId: string;
    roleDropdownLowerBoundary: PermissionRole | string;
    LowerBoundaryRoleMap: ILowerBoundaryRoleMap;
    onUpdatePermission: (params) => void;
    onOpenDeletePermissionDialog: () => void;
    onSetSelectedPermission: (params) => void;
    shouldExcludeOrganizerPermission?: boolean;
    isChangePermissionLoading: boolean;
    selectedPermission: nuorder.folder.v1.Permission;
}

export const generateRoleOptions = (permissionOptions: IDropdownOption[], lowerBoundary: PermissionRole | string) => {
    if (!lowerBoundary) {
        return permissionOptions;
    } else {
        return permissionOptions.map((permissionOption) => {
            const permissionRole = permissionOption.key as string;
            if (PermissionRanking[permissionRole] && comparePermissionRoles(permissionRole, lowerBoundary, "<")) {
                return {
                    ...permissionOption,
                    disabled: true,
                };
            } else {
                return permissionOption;
            }
        });
    }
};

export const PermissionOptionsRenderer: React.FunctionComponent<IOwnProps> = ({
    item,
    roleDropdownLowerBoundary,
    LowerBoundaryRoleMap,
    onSetSelectedPermission,
    merchantId,
    selectedItemId,
    onOpenDeletePermissionDialog,
    onUpdatePermission,
    shouldExcludeOrganizerPermission,
    isChangePermissionLoading,
    selectedPermission,
}) => {
    const [currentRole, setCurrentRole] = React.useState<string>(item?.role);

    const dropdownStyles: Partial<IDropdownStyles> = React.useMemo(
        () => ({
            dropdown: { width: "100%", maxWidth: ASSORTMENT_DROPDOWN_WIDTH, margin: 0, paddingLeft: "8px" },
            title: { margin: 0, border: 0, fontSize: "12px" },
            root: {
                width: "100%",
                maxWidth: ASSORTMENT_DROPDOWN_WIDTH,
                " .ms-Dropdown": {
                    border: 0,
                    margin: 0,
                },
            },
        }),
        []
    );

    const handelRolechange = React.useCallback(
        (value) => {
            onSetSelectedPermission(item);
            if (value === StopSharing) {
                onOpenDeletePermissionDialog();
            } else {
                setCurrentRole(value);
                const params = {
                    merchantId: merchantId,
                    itemId: selectedItemId,
                    type: item.granted_to.kind,
                    role: value,
                    memberId: item.granted_to.member_id,
                    currentPermission: item,
                    LowerBoundaryRoleMap: LowerBoundaryRoleMap,
                    memberMerchantId: item?.granted_to?.merchant_id,
                    originalRole: item?.role,
                };
                onUpdatePermission(params);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            setCurrentRole,
            onUpdatePermission,
            onOpenDeletePermissionDialog,
            item,
            selectedItemId,
            merchantId,
            LowerBoundaryRoleMap,
        ]
    );

    const handleRenderTitle = React.useCallback((options: IDropdownOption[]): JSX.Element => {
        return <span data-test={options[0].key}>{options[0].text}</span>;
    }, []);

    const ownerOption = [{ key: PermissionRole.Owner, text: formatMessage("Owner") }];
    const isOwner = currentRole === PermissionRole.Owner;
    const permissionOptions = generatePermissionOptions(shouldExcludeOrganizerPermission);
    const optionsWithLowerBoundary = generateRoleOptions(permissionOptions, roleDropdownLowerBoundary);
    const stopOption = {
        key: StopSharing,
        text: formatMessage("Remove Access"),
        disabled: !!LowerBoundaryRoleMap[item.granted_to.member_id],
    };
    optionsWithLowerBoundary.push(stopOption);
    const disableOptionsInShare = item.granted_to?.user_id === me._id || isOwner;
    const disableChangePermission = isChangePermissionLoading && item?.id === selectedPermission?.id;
    return (
        <Dropdown
            testId={DATATEST.FOLDERS.GRANT_ACCESS.PERSONA_ITEM_DROPDOWN}
            defaultSelectedKey={currentRole}
            onChange={handelRolechange}
            options={isOwner ? ownerOption : optionsWithLowerBoundary}
            styles={dropdownStyles}
            disabled={disableOptionsInShare || disableChangePermission || item?.deactivated}
            onRenderTitle={handleRenderTitle}
        />
    );
};
